/**
 * Rows that cannot be saved because they hold something but have no name.
 *
 * A fully blank row is still ignored on save (the editors keep a trailing empty
 * row to type into). A row with a value, an update or — for a group item — a
 * subitem but no name used to be dropped silently along with its content; now
 * the editors refuse the save and point at it instead.
 */

const blank = (name) => String(name == null ? '' : name).trim() === '';
const filled = (v) => v !== null && v !== undefined && v !== '';

/** Whether a draft row (subitem editor row, group item or subitem) carries anything worth keeping. */
export function rowHasContent(row) {
  if (!row) return false;
  if (Object.values(row.values || {}).some(filled)) return true;
  if ((row.updates || []).some((u) => u && u.body && u.body.trim() !== '')) return true;
  return (row.subitems || []).some((s) => !blank(s.name) || rowHasContent(s));
}

/** Ids of the subitem editor's rows ({ id, name, values, updates }) that need a name. */
export function unnamedRowIds(rows) {
  return new Set((rows || []).filter((r) => blank(r.name) && rowHasContent(r)).map((r) => r.id));
}

/** Keys of the group draft's items and subitems ({ key, name, values, updates, subitems? }) that need a name. */
export function unnamedGroupKeys(items) {
  const keys = new Set();
  for (const it of items || []) {
    if (blank(it.name) && rowHasContent(it)) keys.add(it.key);
    for (const s of it.subitems || []) if (blank(s.name) && rowHasContent(s)) keys.add(s.key);
  }
  return keys;
}

/** An update card left empty (or only spaces). Saving it would drop it without a word. */
export const isBlankUpdate = (u) => !u || blank(u.body);

/** Ids of the subitem editor's rows with at least one blank update. */
export function blankUpdateRowIds(rows) {
  return new Set((rows || []).filter((r) => (r.updates || []).some(isBlankUpdate)).map((r) => r.id));
}

/** Keys of the group draft's items and subitems with at least one blank update. */
export function blankUpdateGroupKeys(items) {
  const keys = new Set();
  for (const it of items || []) {
    if ((it.updates || []).some(isBlankUpdate)) keys.add(it.key);
    for (const s of it.subitems || []) if ((s.updates || []).some(isBlankUpdate)) keys.add(s.key);
  }
  return keys;
}

/** The message under the table: "2 rows have an empty update." */
export function blankUpdatesMessage(count, noun = 'row') {
  if (!count) return '';
  return count === 1
    ? `1 ${noun} has an empty update. Write it or delete it before saving.`
    : `${count} ${noun}s have an empty update. Write them or delete them before saving.`;
}

/** The message under the table: "2 rows have values but no name." */
export function unnamedRowsMessage(count, noun = 'row') {
  if (!count) return '';
  return count === 1
    ? `1 ${noun} has values but no name. Name it or delete it before saving.`
    : `${count} ${noun}s have values but no name. Name them or delete them before saving.`;
}
